import React from 'react';
import { Typography } from '@mui/material';
import {
	isClient,
	linkObject,
	getDetailLink,
	isOrgUser,
} from '../../../lib/helpers/index';
import API from '../../../lib/API/index';
import { useToast } from '../../../hooks/useToast';
import { linkCustomer, LinkCustomerLinkType } from '../helpers';
import rpcShared from '@rockpapercoin/rpc-shared';
import { LinkObjectType } from '../../../lib/helpers/types';

const resendProposal = async function(
	id: string,
	refresh: () => void,
	hookProps: { showError: ( error: unknown ) => void; showSuccess: () => void }
) {
	// there is no bundling with resends
	const response = await API.sendProposal( id, null );

	if ( !( 'errors' in response ) ) {
		hookProps.showSuccess();
		if ( refresh ) {
			return refresh();
		}
	} else {
		response.errors.map( ( error ) => hookProps.showError( error ) );
		return;
	}
};

type GetProposalDueSoonEventPropsProps = {
	eventProps: {
		event: any;
		message: React.JSX.Element;
		links: {
			type: string;
			text: string;
			link: any;
			onClick?: ( () => any ) | null;
		}[];
		icon: string;
		className: string;
	};
	user: {
		id: string;
		user?: {
			userType?: 'ClientUser' | 'OrgUser' | 'GuestUser' | 'SuperAdmin';
		};
		firstNameOne?: string;
		lastNameOne?: string;
		firstNameTwo?: string;
		lastNameTwo?: string;
	};
	refresh: () => void;
	openModal: ( onGotItOverride?: () => void ) => void;
} & ReturnType<typeof useToast>;

export const getProposalDueSoonEventProps = ( {
	eventProps,
	user,
	refresh,
	showError,
	showSuccess,
	openModal,
}: GetProposalDueSoonEventPropsProps ) => {
	const updatedEventProps = { ...eventProps };
	const proposalLink = linkObject( updatedEventProps.event.proposal );
	const vendorLink = linkObject( updatedEventProps.event.vendor );
	const clientLink = linkCustomer(
		updatedEventProps.event,
		LinkCustomerLinkType.Contact
	);
	updatedEventProps.icon = 'proposal';
	updatedEventProps.links = [
		{
			type: 'secondary',
			text: 'View',
			link: getDetailLink( {
				object: updatedEventProps.event.proposal,
				type: LinkObjectType.Proposal,
			} ),
		},
	];

	const getResendOrViewLink = () => {
		return {
			type: updatedEventProps.event.resolvedAt ? 'secondary' : 'primary',
			text: updatedEventProps.event.resolvedAt ? 'View' : 'Resend',
			link: updatedEventProps.event.resolvedAt
				? getDetailLink( {
					object: updatedEventProps.event.contract,
					type: LinkObjectType.Contract,
				  } )
				: null,
			onClick: updatedEventProps.event.resolvedAt
				? null
				: () => {
					const performResend = () =>
						resendProposal( updatedEventProps.event.proposal.id, refresh, {
							showError,
							showSuccess,
						} );
					if (
						updatedEventProps.event.proposal?.automatedRemindersEnabled ===
							false
					) {
						openModal( performResend );
						return;
					}
					performResend();
				  },
		};
	};

	if ( isClient( user ) ) {
		updatedEventProps.message = (
			<Typography className='p--secondary'>
				{ proposalLink } from { vendorLink } is due soon.
			</Typography>
		);
	} else if ( updatedEventProps.event.contact.customer.clientUser ) {
		if ( updatedEventProps.event.orgUser.id !== user.id ) {
			updatedEventProps.message = (
				<Typography className='p--secondary'>
					{ proposalLink } from { vendorLink } is due soon.
				</Typography>
			);
		} else {
			updatedEventProps.message = (
				<Typography className='p--secondary'>
					{ proposalLink } is due soon. Resend?
				</Typography>
			);
			updatedEventProps.links = [ getResendOrViewLink() ];
		}
	} else if ( isOrgUser( user ) ) {
		// if the user is admin of the event's org then they can resend if the event is unresolved
		const isUserAdminOrManagerOfSendingOrg =
			rpcShared.contacts.isAdminOrManagerOfVendor(
				updatedEventProps.event.contact,
				user
			);
		const isAssignedPlanner = rpcShared.contacts.isAssignedPlanner(
			updatedEventProps.event.contact,
			user
		);
		if ( isUserAdminOrManagerOfSendingOrg ) {
			updatedEventProps.message = (
				<Typography className='p--secondary'>
					{ proposalLink } is due soon. Resend?
				</Typography>
			);
			updatedEventProps.links = [ getResendOrViewLink() ];
			// if the user is NOT admin of the event's org, all they can do is view
		} else if ( isAssignedPlanner ) {
			updatedEventProps.message = (
				<Typography className='p--secondary'>
					A proposal from { vendorLink } that was sent to { clientLink } is due
					soon: { proposalLink }.
				</Typography>
			);
		} else {
			updatedEventProps.message = (
				<Typography className='p--secondary'>
					{ proposalLink } is due soon.
				</Typography>
			);
		}
	}

	return updatedEventProps;
};
